import { useEffect, useState } from 'react';
import { useLocale } from '../../context';
import CodeInput from './CodeInput';
import AuthCodeHelpSheet from './AuthCodeHelpSheet';
import useOtpResendCooldown, { OTP_RESEND_COOLDOWN_SEC } from '../../hooks/useOtpResendCooldown';

export default function LoginVerification({
  challenge,
  step,
  onGetCode,
  onCancel,
  onVerify,
  onResendCode,
  loading,
  error,
  noAccessHref,
  noAccessLabel,
}) {
  const _locale = useLocale();
  const tr = _locale.t;
  const [code, setCode] = useState('');
  const [helpOpen, setHelpOpen] = useState(false);
  const [resendBusy, setResendBusy] = useState(false);
  const { secondsLeft, canResend, startCooldown, resetCooldown } = useOtpResendCooldown(OTP_RESEND_COOLDOWN_SEC);

  useEffect(() => {
    if (step !== 'code') {
      setCode('');
      setHelpOpen(false);
      resetCooldown();
      return;
    }
    startCooldown(OTP_RESEND_COOLDOWN_SEC);
  }, [step, challenge?.id, resetCooldown, startCooldown]);

  useEffect(() => {
    if (error) setCode('');
  }, [error]);

  const handleComplete = (completeCode) => {
    if (loading || completeCode.length < 6) return;
    onVerify(completeCode);
  };

  const handleResend = async () => {
    if (!canResend || loading || resendBusy || typeof onResendCode !== 'function') return;
    setResendBusy(true);
    try {
      const result = await onResendCode();
      const retryAfter = Number(result?.retryAfter) || OTP_RESEND_COOLDOWN_SEC;
      startCooldown(retryAfter);
    } catch (err) {
      const retryAfter = Number(err?.meta?.retryAfter || err?.retryAfter);
      if (retryAfter > 0) startCooldown(retryAfter);
    } finally {
      setResendBusy(false);
    }
  };

  const methodText = challenge.contactType === 'phone'
    ? tr('auth.verify.methodPhone', { contact: challenge.maskedContact })
    : tr('auth.verify.methodEmail', { contact: challenge.maskedContact });

  const accessHref = noAccessHref || '/help-center/sss/personal-data/recover-password';
  const accessLabel = noAccessLabel
    || (challenge.contactType === 'email'
      ? tr('auth.verify.noAccessEmail')
      : tr('auth.verify.noAccess'));

  if (step === 'code') {
    const codeInfo = challenge.contactType === 'phone'
      ? tr('auth.verify.codeInfoPhone', { contact: challenge.maskedContact })
      : tr('auth.verify.codeInfoEmail', { contact: challenge.maskedContact });

    const resendLabel = canResend
      ? tr('auth.verify.resend')
      : tr('auth.verify.resendIn', { seconds: secondsLeft });

    return (
      <>
        <span className="auth-verify__info auth-verify__info--code">{codeInfo}</span>
        <button
          type="button"
          className="auth-verify__never-received"
          disabled={loading}
          onClick={() => setHelpOpen(true)}
        >
          {tr('auth.verify.neverReceived')}
        </button>
        <div className="auth-verify__box auth-verify__box--code">
          <CodeInput
            value={code}
            onChange={setCode}
            onComplete={handleComplete}
            error={Boolean(error)}
            disabled={loading}
          />
          {error && <div className="auth-verify__error">{error}</div>}
          <div className="auth-verify__actions">
            <button
              type="button"
              className="auth-submit"
              disabled={loading || code.length < 6}
              onClick={() => onVerify(code)}
            >
              {loading ? '...' : tr('auth.verify.continue')}
            </button>
            {typeof onResendCode === 'function' && (
              <button
                type="button"
                className="auth-verify__resend"
                disabled={loading || resendBusy || !canResend}
                onClick={() => {
                  handleResend();
                }}
              >
                {resendBusy ? '...' : resendLabel}
              </button>
            )}
            <button type="button" className="auth-verify__cancel" disabled={loading} onClick={onCancel}>
              {tr('auth.verify.cancel')}
            </button>
          </div>
        </div>
        <AuthCodeHelpSheet
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          contactType={challenge.contactType}
        />
      </>
    );
  }

  return (
    <>
      <span className="auth-verify__info">{tr('auth.verify.info')}</span>
      <div className="auth-verify__box">
        <span className="auth-verify__caption">{tr('auth.verify.caption')}</span>
        <span className="auth-verify__method">{methodText}</span>
        <div className="auth-verify__actions">
          <button type="button" className="auth-submit" disabled={loading} onClick={onGetCode}>
            {loading ? '...' : tr('auth.verify.getCode')}
          </button>
          <button type="button" className="auth-verify__cancel" disabled={loading} onClick={onCancel}>
            {tr('auth.verify.cancel')}
          </button>
        </div>
      </div>
      <a
        href={accessHref}
        className="auth-verify__no-access"
        onClick={(e) => {
          if (accessHref.startsWith('/')) return;
          e.preventDefault();
        }}
      >
        {accessLabel}
      </a>
    </>
  );
}
